import { UserIcon, KeyIcon, ArrowLeftStartOnRectangleIcon } from '@heroicons/react/24/outline';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button, Avatar } from '@nextui-org/react';
import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { UpdatePasswordModal } from '@portal/components/UpdatePasswordModal';
import { tsrestClient } from '@portal/provider/tsrest';
import { useAuthStatus } from '@portal/utils';

export const UserDropdown = () => {
  const { user } = useAuthStatus();
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({ oldPassword: '', password: '', confirmPassword: '' });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const logoutMutation = tsrestClient.auth.logout.useMutation({
    onSettled: () => {
      localStorage.removeItem('token');
      window.location.href = '/login';
    },
  });

  const updatePasswordMutation = tsrestClient.auth.update.useMutation({
    onSuccess: () => {
      toast('Password updated successfully', { type: 'success' });
      setIsPasswordModalOpen(false);
      setPasswordData({ oldPassword: '', password: '', confirmPassword: '' });
      setShowOldPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    },
    onError: error => {
      const errorMessage = (error.body as any)?.message || 'Unknown error';
      toast(`Failed to update password: ${errorMessage}`, { type: 'error' });
    },
  });

  const onLogout = () => {
    logoutMutation.mutate({
      body: {
        token: localStorage.getItem('token') || '',
      },
    });
  };

  const onUpdatePassword = () => {
    // Validation
    if (!passwordData.oldPassword) {
      toast('Current password is required', { type: 'error' });
      return;
    }

    if (!passwordData.password) {
      toast('New password is required', { type: 'error' });
      return;
    }

    if (passwordData.password !== passwordData.confirmPassword) {
      toast('New passwords do not match', { type: 'error' });
      return;
    }

    updatePasswordMutation.mutate({
      body: {
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.password,
      },
      headers: {
        authorization: localStorage.getItem('token') || '',
      },
    });
  };

  return (
    <>
      <Dropdown placement='bottom-end'>
        <DropdownTrigger>
          <Button variant='light' isIconOnly className='w-10 h-10 min-w-10 p-0'>
            <Avatar
              name={user}
              size='md'
              className='w-8 h-8 text-sm font-semibold border-2 border-blue-300 hover:border-blue-500 transition-all duration-200 bg-blue-500 text-white shadow-md hover:shadow-lg'
              showFallback
              fallback={<UserIcon className='h-5 w-5 text-white' />}
            />
          </Button>
        </DropdownTrigger>
        <DropdownMenu aria-label='User actions'>
          <DropdownItem
            key='update-password'
            startContent={<KeyIcon className='h-4 w-4' />}
            onPress={() => setIsPasswordModalOpen(true)}
          >
            Update Password
          </DropdownItem>
          <DropdownItem
            key='logout'
            startContent={<ArrowLeftStartOnRectangleIcon className='h-4 w-4' />}
            color='danger'
            onPress={onLogout}
          >
            Logout
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      <UpdatePasswordModal
        isOpen={isPasswordModalOpen}
        onOpenChange={() => setIsPasswordModalOpen(false)}
        passwordData={passwordData}
        onPasswordDataChange={setPasswordData}
        showOldPassword={showOldPassword}
        showNewPassword={showNewPassword}
        showConfirmPassword={showConfirmPassword}
        onShowOldPasswordChange={setShowOldPassword}
        onShowNewPasswordChange={setShowNewPassword}
        onShowConfirmPasswordChange={setShowConfirmPassword}
        onSubmit={onUpdatePassword}
        isLoading={updatePasswordMutation.isLoading}
      />
    </>
  );
};
